import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useRBAC } from '@/hooks/useRBAC';
import { Action, Resource } from '@/lib/rbac/permissions';
import { useIsFeatureEnabled, FeatureFlag } from '@/config/featureFlags';

interface RouteGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  requireFlag?: FeatureFlag;
  action?: Action;
  resource?: Resource;
  fallbackPath?: string;
}

/**
 * Centralized route guard using canonical RBAC
 */
export function RouteGuard({ 
  children, 
  requireAuth = false,
  requireAdmin = false,
  requireFlag,
  action,
  resource,
  fallbackPath = '/'
}: RouteGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, can, loading: rbacLoading } = useRBAC();
  const featureEnabled = useIsFeatureEnabled(requireFlag || 'enable_dashboard');
  const location = useLocation();

  const loading = authLoading || rbacLoading;

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  // Check authentication — preserve current URL as redirect
  if (requireAuth && !user) {
    const currentPath = location.pathname + location.search;
    const authUrl = `/auth?mode=signin&redirect=${encodeURIComponent(currentPath)}`;
    return <Navigate to={authUrl} replace />;
  }

  // Check admin role
  if (requireAdmin && !isAdmin) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check feature flag
  if (requireFlag && !featureEnabled) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check specific permission
  if (action && resource && !can(action, resource)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
